import {
  useGetCurrentUserQuery,
  useGetOrganizationQuery,
} from '@carboncopyco/data';
import {
  type DemoShare,
  type IDemo,
  type IPresentation,
  type IUser,
  type IVariable,
  type Opportunity,
  type ShareSecurity,
  ShareSecurityLevel,
  type WelcomeMatConfig,
} from '@carboncopyco/types';
import {
  Button,
  InputTextField,
  LoadingDots,
  Modal,
  type ModalProps,
  SubmitButton,
  TextSubHeader,
} from '@carboncopyco/ui';
import { isImageVariable } from '@carboncopyco/utils';
import { useBoolean } from 'ahooks';
import { Form, Formik, type FormikHelpers } from 'formik';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useCallback, useMemo, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { array, boolean, number, object, string } from 'yup';
import { useIntegration } from '../Integrations';
import { EMAIL_DOMAIN_REGEXP, ValidationErrorMessages } from '../consts';
import { useFilesServiceContext } from '../utils';
import { AdvancedSettings } from './AdvancedSettings';
import { ClientNameField } from './ClientNameField';
import { ExpirationSection } from './ExpirationSection';
import { FilterEmailsField } from './FilterEmailsField';
import { FormPreviewModal } from './FormPreviewModal';
import { GuidedSandboxSection } from './GuidedSandboxSection';
import { IntegrationLeadsField } from './IntegrationLeadsField';
import { SecurityField } from './SecurityField';
import { SlackIntegrationField } from './SlackIntegrationField';
import { VariablesSection } from './VariableSection';
import { WelcomeMatField } from './WelcomeMatField';
import {
  WELCOME_MAT_TITLE_MAX_LENGTH,
  WelcomeMatTitleField,
} from './WelcomeMatTitleField';
import { type SecurityItem, getAccessLevels } from './consts';
import { useAllowedShareSecurityLevels } from './useAllowedShareSecurityLevels';

export type ShareDemoModalState = {
  client: IPresentation['forClient'];
  opportunity?: Opportunity;
  linkName: IPresentation['presentationName'];
  isActive: DemoShare['isActive'];
  skipWelcome: DemoShare['options']['skipWelcome'];
  expiresAt: DemoShare['options']['expiresAt'];
  daysToExpire: DemoShare['options']['daysToExpire'];
  security: ShareSecurity;
  variables: IVariable[];
  toggleExpiration: boolean;
  cover: WelcomeMatConfig;
  salesforceLeads: boolean;
  hubspotLeads: boolean;
  slackIntegration: boolean;
  showGuidanceTooltip: DemoShare['options']['showGuidanceTooltip'];
  welcomeMatTitle: string;
};

export type ShareDemoModalProps = Required<
  Pick<ModalProps, 'title' | 'open' | 'onClose'>
> & {
  demoId: string;
  initialValues: ShareDemoModalState;
  handleSubmit: (
    values: ShareDemoModalState,
    formikHelpers: FormikHelpers<ShareDemoModalState>
  ) => void;
  isSharedFromDemo?: boolean;
  isLoading?: boolean;
  yesLabel?: string;
};

export const getInitialShareDemoModalValues = (
  demoName: IDemo['demoName'],
  user: IUser,
  variables: IVariable[],
  welcomeMatTitle?: string
): ShareDemoModalState => ({
  linkName: `Link for ${demoName}`,
  skipWelcome: false,
  security: {
    emailDomain: user?.email.split('@').slice(-1)[0],
    level: ShareSecurityLevel.internal,
  },
  client: 'internal',
  opportunity: null,
  isActive: false,
  expiresAt: null,
  daysToExpire: null,
  toggleExpiration: false,
  variables: variables.toReversed(),
  cover: { requireName: false, requireEmail: true },
  salesforceLeads: false,
  hubspotLeads: false,
  slackIntegration: false,
  showGuidanceTooltip: false,
  welcomeMatTitle,
});

export const ShareDemoModal = ({
  demoId,
  open,
  onClose,
  title,
  initialValues,
  handleSubmit,
  isSharedFromDemo,
  isLoading,
  yesLabel = 'Create link',
}: ShareDemoModalProps) => {
  const flags = useFlags();
  const shareDemoBaseSchema = useMemo(
    () =>
      object({
        linkName: string()
          .required(ValidationErrorMessages.required)
          .max(200, 'Link description must be at most 200 characters'),
        skipWelcome: boolean(),
        client: string().required(ValidationErrorMessages.required),
        security: object({
          level: string(),
          emailDomain: string().when('level', {
            is: 'email_domain',
            then: (schema) =>
              schema
                .required(ValidationErrorMessages.required)
                .matches(
                  EMAIL_DOMAIN_REGEXP,
                  ValidationErrorMessages.invalidEmailDomain
                ),
          }),
          emails: array().when('level', {
            is: 'restricted',
            then: (schema) =>
              schema
                .required(ValidationErrorMessages.required)
                .of(string().email('Invalid email'))
                .min(1, 'Please fill out at least one email address'),
          }),
        }),
        isActive: boolean(),
        daysToExpire: number()
          .nullable()
          .min(1, 'Expiration date must be 1 day or more from today')
          .max(999, 'Expiration date must be within 1,000 days'),
        variables: array().optional(),
        welcomeMatTitle: string()
          .required(ValidationErrorMessages.required)
          .max(
            WELCOME_MAT_TITLE_MAX_LENGTH,
            `Exceeds max length of ${WELCOME_MAT_TITLE_MAX_LENGTH} characters`
          ),
      }),
    [flags]
  );
  const { organizationId } = useParams();
  const { data: organization } = useGetOrganizationQuery({
    id: organizationId,
  });
  const { filesServiceDomain } = useFilesServiceContext();
  const { data: userResponse } = useGetCurrentUserQuery();
  const { user } = userResponse || {};
  const [showFormPreviewModal, setShowFormPreviewModal] = useBoolean(false);
  const modifiedInitialValues = useMemo(
    () => ({
      ...initialValues,
      variables: initialValues.variables?.map((variable) => {
        if (isImageVariable(variable)) {
          return {
            ...variable,
            value: `${filesServiceDomain}${variable.value}`,
          };
        }

        return variable;
      }),
      client: initialValues.client ?? 'internal',
    }),
    [initialValues, filesServiceDomain]
  );

  const { disabled } = useIntegration({
    integrationType: 'slack',
    organizationId,
  });

  const slackIntegrationInitialState = disabled
    ? false
    : !flags.slackIntegrationControl;

  const handleSecurityLevelChange = useCallback(
    (level: ShareSecurityLevel, values) => {
      const { security, ...oldValues } = values;
      const newValues = {
        ...oldValues,
        security: { level },
      };
      switch (level) {
        case 'internal':
          newValues.skipWelcome = false;
          newValues.client = isSharedFromDemo ? 'internal' : oldValues.client;
          newValues.security.emailDomain = user?.email.split('@').slice(-1)[0];
          newValues.salesforceLeads = false;
          newValues.hubspotLeads = false;
          newValues.slackIntegration = false;
          newValues.opportunity = null;
          break;
        case 'email_domain':
          newValues.skipWelcome = false;
          newValues.slackIntegration = slackIntegrationInitialState;
          break;
        case 'restricted':
          newValues.skipWelcome = false;
          newValues.slackIntegration = slackIntegrationInitialState;
          break;
        case 'anyone':
          newValues.client = isSharedFromDemo ? 'anyone' : oldValues.client;
          newValues.slackIntegration = slackIntegrationInitialState;
          newValues.security.filterEmailDomains = !!security.filterEmailDomains;
          break;
      }

      return newValues;
    },
    [user, isSharedFromDemo, slackIntegrationInitialState]
  );

  const accessLevels = useMemo(getAccessLevels, []);
  const { allowedDemoSecurityLevels = [] } =
    useAllowedShareSecurityLevels(organizationId);
  const items: SecurityItem[] = Object.values(ShareSecurityLevel)
    .filter((level) =>
      flags.deprecateAnyoneSecurityLevel ? level !== 'anyone' : true
    )
    .map((level) => ({
      ...accessLevels[level],
      disabled: !allowedDemoSecurityLevels.includes(level),
    }));
  const submitButtonRef = useRef<HTMLDivElement>(null);

  return (
    <Modal
      open={open}
      title={
        <div className="flex items-center">
          <TextSubHeader
            label={title}
            color="text-ds-natural-600"
          />
        </div>
      }
      onClose={onClose}
    >
      <Formik<ShareDemoModalState>
        validationSchema={shareDemoBaseSchema}
        onSubmit={handleSubmit}
        initialValues={modifiedInitialValues}
      >
        {({ values }) => (
          <Form>
            <div className="flex flex-col gap-3 w-[560px] mt-3">
              <div>
                {flags.welcomeMatTitle && <WelcomeMatTitleField />}
                <InputTextField
                  label="Link name"
                  required
                  name="linkName"
                  validateFieldOnBlur
                  clearOnFocus
                  inputSize="lg"
                />
                {isSharedFromDemo && <ClientNameField />}
                <div className="flex flex-col gap-3">
                  <div className="w-full">
                    <SecurityField<ShareDemoModalState>
                      onSecurityLevelChange={handleSecurityLevelChange}
                      items={items}
                    />
                  </div>
                  {flags.filterEmailDomains &&
                    values.security.level === 'anyone' &&
                    !flags.deprecateAnyoneSecurityLevel && (
                      <FilterEmailsField />
                    )}
                  <AdvancedSettings mountWhenHidden>
                    <div className="flex flex-col">
                      <ExpirationSection />
                      <GuidedSandboxSection
                        organizationId={organizationId}
                        demoId={demoId}
                      />
                      <IntegrationLeadsField
                        securityLevel={values.security.level}
                        vendor="salesforce"
                        infoText="Every visitor will become a Lead in Salesforce. Your Salesforce Admin can configure SFDC to convert that Lead into a Contact and add them as a Contact role on your open Opp."
                      />
                      {flags?.hubspotIntegration && (
                        <IntegrationLeadsField
                          securityLevel={values.security.level}
                          vendor="hubspot"
                          infoText="Every visitor will become a Contact in HubSpot. Get a detailed view of who is engaging with your product"
                        />
                      )}
                      {flags?.slackIntegrationControl && (
                        <SlackIntegrationField
                          securityLevel={values.security.level}
                        />
                      )}
                    </div>
                    <WelcomeMatField
                      allowWelcomeMatConfigChange={
                        flags?.shareDemoCoverPageControl
                      }
                      handlePreviewCoverPage={setShowFormPreviewModal.setTrue}
                    />
                  </AdvancedSettings>
                </div>
              </div>

              <div className="flex flex-col gap-6">
                {!!values.variables.length && (
                  <VariablesSection
                    variables={values.variables}
                    readOnly={!isSharedFromDemo}
                  />
                )}
                {isLoading ? (
                  <div className="flex justify-center">
                    <LoadingDots />
                  </div>
                ) : (
                  <div className="flex justify-end gap-2">
                    <Button
                      secondary
                      label="Cancel"
                      onClick={onClose}
                    />
                    <SubmitButton<ShareDemoModalState>
                      render={({ isValid }) => (
                        <div ref={submitButtonRef}>
                          <Button
                            disabled={!isValid}
                            label={yesLabel}
                            submit
                          />
                        </div>
                      )}
                    />
                  </div>
                )}
              </div>
            </div>
            {user && organization && (
              <FormPreviewModal
                welcomeMatConfig={values.cover}
                open={showFormPreviewModal}
                assetType="demo"
                organizationName={organization.organizationName}
                presenterName={user.name}
                organizationLogo={organization.logo}
                emailDomain={values.security.emailDomain}
                securityLevel={values.security.level}
                onClose={setShowFormPreviewModal.setFalse}
                welcomeMatTitle={values.welcomeMatTitle || 'Title appears here'}
              />
            )}
          </Form>
        )}
      </Formik>
    </Modal>
  );
};
